import { useState, useEffect } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';

import { IRepository, IResourceVersion, IRepositoryFile, IRepositoryFileRequest } from '@/types';
import { IResponseError } from '@/lib/apiBase';
import { getFileContent, IRepositoryFileResponse, TDownloadProgress } from '@/lib/repositoryFileApi';
import { FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';
import { URLSearchParams } from 'url';

interface useRepositoryFileParams {
  repository: IRepository;
  resource?: IResourceVersion;
  filename?: string;
  dataType: string;
  mimeType: string;
  query?: URLSearchParams;
  initialData?: IRepositoryFileResponse | undefined;
  onProgress?: (progress: TDownloadProgress) => void;
}

const useRepositoryFile = ({
  repository,
  resource,
  filename,
  dataType,
  mimeType,
  query,
  initialData,
  onProgress,
}: useRepositoryFileParams) => {
  const token = Cookies.get('oxen_user_token');

  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [file, setFile] = useState<IRepositoryFile | undefined>(initialData?.file);
  const [error, setError] = useState<IResponseError | undefined>();

  const branch = resource?.version || 'main';
  const path = resource?.path;
  const pathWithBranch = [branch, path].join('/');

  const fileInfo: IRepositoryFileRequest = {
    repositoryName: repository.name,
    namespace: repository.namespace,
    resource: pathWithBranch,
    dataType: dataType,
    mimeType: mimeType,
  };

  // TODO: Error is currently handled by the API method
  // should this be exposed to useSWR so that we can take advantage of SWR functionality such as retry and reporting

  const { data, isLoading, isValidating } = useSWR(
    [repository.namespace, repository.name, pathWithBranch, filename, dataType, query],
    () =>
      getFileContent({
        fileInfo,
        query: query as URLSearchParams,
        token,
        emitProgress: (progess: TDownloadProgress) => onProgress?.(progess),
      }),
    {
      fallbackData: initialData,
      keepPreviousData: true,
      revalidateOnFocus: false,
      // revalidateOnMount: false,
      revalidateOnReconnect: false,
      // focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
    },
  );

  useEffect(() => {
    if (data) {
      setFile(data?.file);
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  return {
    status: status,
    isLoading: isLoading,
    isValidating: isValidating,
    file: file,
    resource: resource,
    error: error,
  };
};

export default useRepositoryFile;
